// Nursing Manager · Bed Assignments. Assign nurses to beds, transfer patients
// between beds, and view live occupancy + transfer history. Wired to the
// roster / assignment / bed / transfer-log endpoints; bed transfers are
// raised via RaiseTransferRequestModal (the approve-then-execute bed
// transfer requisition flow — the old direct-execute edit_bed /
// cross_department_bed_transfer endpoints are retired).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar, Box, Button, Chip, Grid, IconButton, Paper, Stack, Tooltip, Typography, LinearProgress, Snackbar, Alert,
  TextField, MenuItem, ToggleButton, ToggleButtonGroup, Checkbox, ListItemText, OutlinedInput, Select, InputLabel, FormControl,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import EventBusyIcon from "@mui/icons-material/EventBusy";

import { useNurseData } from "../NurseDataContext";
import { useDebouncedValue, useShiftSchedule } from "../hooks";
import { NURSE_COLORS, initials, nurseAvatarColor } from "../theme";
import {
  getRoster, getAssignments, assignBed, bulkAssignBeds, unassignBed,
  getSummary, listOf,
} from "../api/nurseApi";
import useBedCensusData from "../../ipd/hooks/useBedCensusData";
import BedCensusTable from "../../ipd/components/BedCensusTable";
import BedCensusFilters from "../../ipd/components/BedCensusFilters";
import WardRatioStrip from "../components/WardRatioStrip";
import RaiseTransferRequestModal from "../../ipd/pages/bed-transfer-requisition/RaiseTransferRequestModal";
import RetainBedModal from "../../ipd/pages/bed-retention/RetainBedModal";
import { currentShift, isPinInForce } from "../shiftWindow";
import { usePermissions } from "../../login/PermissionContext";
import { isActionAllowedForRole, BED_DASHBOARD_ACTIONS } from "../../ipd/utils/bedDashboardRoles";

// Defensive accessor — the Bed serializer shape can vary.
const bedCodeOf = (b) => b.code || b.bed_id || b.bed_code || String(b.id ?? "");


export default function BedDashboard() {
  const { patients, reloadPatients } = useNurseData();
  const {
    beds, admissionByBed, loading: bedsLoading, reload: reloadBeds,
    filters, setFilters, search, setSearch,
    wards, rooms,
  } = useBedCensusData();
  const { userInfo } = usePermissions();
  const roleName = userInfo?.role_permission?.[0]?.role;
  const can = (action) => isActionAllowedForRole(action, roleName);

  // The census search lives in the shared useBedCensusData hook (also used by
  // IPD screens), so debounce locally: the input binds to searchInput for
  // instant typing; the hook's filter only sees the settled value.
  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearch = useDebouncedValue(searchInput);
  useEffect(() => { setSearch(debouncedSearch); }, [debouncedSearch, setSearch]);

  const [nurses, setNurses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNurseId, setSelectedNurseId] = useState("");
  const [toast, setToast] = useState(null);

  const [xfer, setXfer] = useState(null); // { admissionId, fromBedCode, fromBed }
  const [retainCode, setRetainCode] = useState(null);

  // Ward-assignment mode: assign the selected nurse(s) to every bed in a ward
  // in one bulk call, instead of clicking bed-by-bed. "Ward" here is the
  // room_category filter (ICU / General / Private) under a department — see
  // useBedCensusData's hierarchy note. Coverage is shared: each selected nurse
  // is assigned to all beds in the ward.
  const [mode, setMode] = useState("bed"); // "bed" | "ward"
  const [wardNurseIds, setWardNurseIds] = useState([]);
  const [wardBusy, setWardBusy] = useState(false);

  // The shift in progress — what "in force" means for the counts below. Fixed
  // for the life of the screen; distinct from the pinFrom/pinTo/pinShift the
  // manager edits, which describe the pin they are about to CREATE.
  const { schedule } = useShiftSchedule();
  const nowShift = useMemo(() => currentShift(new Date(), schedule), [schedule]);

  // Every pin now needs a window. Default to the shift in progress, today
  // only — the safest default is the narrowest one, so a manager who ignores
  // these controls creates a pin that expires tonight rather than never.
  const [pinFrom, setPinFrom] = useState(nowShift.rosterDate);
  const [pinTo, setPinTo] = useState(nowShift.rosterDate);
  const [pinShift, setPinShift] = useState(nowShift.shift);

  // The first render resolves against DEFAULT_SCHEDULE; once the facility's own
  // schedule arrives the defaults must follow it, or the pre-selected shift
  // could be a code this tenant does not define.
  useEffect(() => {
    setPinShift(nowShift.shift);
    setPinFrom(nowShift.rosterDate);
    setPinTo(nowShift.rosterDate);
  }, [nowShift.shift, nowShift.rosterDate]);

  const reloadAssignments = useCallback(
    () => getAssignments().then((r) => setAssignments(listOf(r))).catch(() => {}),
    []
  );

  const reloadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      getRoster().then((r) => listOf(r)).catch(() => []),
      getAssignments().then((r) => listOf(r)).catch(() => []),
      getSummary().then((r) => r?.data || null).catch(() => null),
    ]).then(([ns, as_, sm]) => {
      setNurses(ns);
      setAssignments(as_);
      setSummary(sm);
      // No auto-select — pre-selecting a nurse invites assigning the wrong one.
      // The manager picks a nurse explicitly; the + button is disabled until then.
      setSelectedNurseId((cur) => cur || "");
      setLoading(false);
    });
  }, []);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  // Occupancy for the transfer dialog's vacant-bed calc — from the nurse
  // dashboard's own admission census (already scoped to status=Allocated),
  // kept separate from useBedCensusData()'s admissionByBed (used for the
  // shared table's display columns) rather than merging the two sources.
  const patientByBed = useMemo(() => {
    const map = {};
    patients.forEach((p) => { if (p.bed) map[p.bed] = p; });
    return map;
  }, [patients]);

  const assignmentsByBed = useMemo(() => {
    const map = {};
    // One chip per nurse per bed — defensive dedupe for any legacy per-shift
    // duplicate rows that predate the one-active-assignment rule (the backend
    // now dedupes on (nurse, bed) and a partial unique index backs it).
    assignments.forEach((a) => {
      const list = (map[a.bed_code] = map[a.bed_code] || []);
      if (!list.some((x) => x.nurse_id === a.nurse_id)) list.push(a);
    });
    return map;
  }, [assignments]);

  // Resolves an assignment's nurse_id to a name when the row itself didn't
  // carry one — never let the raw hex id leak into the UI.
  const nurseNameById = useMemo(() => {
    const map = {};
    nurses.forEach((n) => { map[n.nurse_id] = n.name; });
    return map;
  }, [nurses]);

  // Workload means NOW. getAssignments() deliberately returns every active pin
  // — a manager planning next week needs to see future and past ones — but the
  // "N beds" chip reads as current load, so it must count only the pins in
  // force this shift. Counting the raw list showed a nurse "14 beds" when 13 of
  // them had expired (2026-08-14).
  const nurseLoad = (nurseId) =>
    assignments.filter((a) => a.nurse_id === nurseId && isPinInForce(a, nowShift.rosterDate, nowShift.shift)).length;
  // Returns the assignment row (carries the `id` unassign needs), not just a boolean.
  const findAssignment = (nurseId, bedCode) => assignments.find((a) => a.nurse_id === nurseId && a.bed_code === bedCode);

  const stats = {
    total: summary?.beds?.total ?? beds.length,
    occupied: summary?.beds?.occupied ?? Object.keys(patientByBed).length,
    // Live coverage, so in-force pins only — an expired pin covers nobody.
    withNurse: new Set(
      assignments.filter((a) => isPinInForce(a, nowShift.rosterDate, nowShift.shift)).map((a) => a.bed_code)
    ).size,
    nurses: nurses.length,
  };

  const doAssign = async (bedCode) => {
    if (!selectedNurseId) { setToast({ sev: "warning", msg: "Select a nurse first" }); return; }
    if (!pinFrom || !pinTo || pinTo < pinFrom) { setToast({ sev: "warning", msg: "Set a valid assignment window (To cannot be before From)" }); return; }
    try {
      // Assignment is NOT roster-gated (2026-07-24): a nurse can be pinned to
      // any bed regardless of roster. It IS time-gated (2026-08-14): the pin
      // only grants coverage for the chosen shift on the chosen dates.
      const payload = {
        nurse_id: selectedNurseId, bed_code: bedCode,
        valid_from: pinFrom, valid_to: pinTo, shift: pinShift,
      };
      const res = await assignBed(payload);
      if (res?.status !== "Success") {
        setToast({ sev: "error", msg: res?.message || "Assign failed" });
        return;
      }
      // Roster-gate rejections ride in `errors` on a Success envelope.
      if (Array.isArray(res?.errors) && res.errors.length > 0) {
        setToast({ sev: "error", msg: res.errors[0]?.error || "Assign rejected" });
        return;
      }
      // Single-assign path returns status "Success" with an empty `data`
      // list (no error) when the (nurse, bed, shift) row already exists —
      // dedupe is silent, not a "Failed" envelope. Catch it here so a no-op
      // doesn't surface as "Nurse assigned".
      if (Array.isArray(res?.data) && res.data.length === 0) {
        setToast({ sev: "warning", msg: "Nurse is already assigned to this bed" });
        return;
      }
      await reloadAssignments();
      setToast({ sev: "success", msg: "Nurse assigned" });
    } catch (err) { setToast({ sev: "error", msg: err?.response?.data?.message || "Assign failed" }); }
  };
  const doUnassign = async (assignment) => {
    if (!assignment) { setToast({ sev: "error", msg: "Remove failed" }); return; }
    if (!window.confirm(`Remove ${assignment.nurse_name || nurseNameById[assignment.nurse_id] || assignment.nurse_id} from ${assignment.bed_code}?`)) return;
    try {
      // Prefer the id-based delete — it's precise (deactivates exactly this
      // shift-row). The compound {nurse_id, bed_code} form now deactivates
      // ALL shift-rows for that pair, so it's only a fallback for the case
      // where a row somehow has no id.
      const res = assignment.id != null
        ? await unassignBed({ id: assignment.id })
        : await unassignBed({ nurse_id: assignment.nurse_id, bed_code: assignment.bed_code, shift: assignment.shift });
      if (res?.status !== "Success") {
        setToast({ sev: "error", msg: res?.message || "Remove failed" });
        return;
      }
      await reloadAssignments();
      setToast({ sev: "success", msg: "Assignment removed" });
    } catch (err) { setToast({ sev: "error", msg: err?.response?.data?.message || "Remove failed" }); }
  };

  // ---- Transfer requisition (ADR 0003 — approval required, no direct-execute) ----
  const openTransfer = (bed) => {
    const patient = patientByBed[bedCodeOf(bed)];
    if (!patient?.admissionId) {
      setToast({ sev: "error", msg: "Missing admission for this bed" });
      return;
    }
    setXfer({ admissionId: patient.admissionId, fromBedCode: bedCodeOf(bed), fromBed: bed });
  };
  const handleRaised = async () => {
    setXfer(null);
    await Promise.all([reloadPatients(), reloadAssignments()]);
    setToast({ sev: "success", msg: "Transfer request raised — awaiting approval" });
  };

  const selectedNurse = nurses.find((n) => n.nurse_id === selectedNurseId);

  // ---- Ward assignment (bulk) ----
  // The beds in scope are exactly the ones shown in the table below — the
  // census `beds` already reflect the active department/ward filters, so the
  // manager assigns what they see (WYSIWYG). A ward (room_category) filter is
  // required so this can't accidentally blanket an entire department.
  const wardName = wards.find((w) => w.code === filters.room_category)?.name || filters.room_category;
  const wardBedCodes = useMemo(() => beds.map(bedCodeOf), [beds]);

  const doAssignWard = async () => {
    if (!filters.room_category) { setToast({ sev: "warning", msg: "Select a Ward first" }); return; }
    if (!wardNurseIds.length) { setToast({ sev: "warning", msg: "Select at least one nurse" }); return; }
    if (!wardBedCodes.length) { setToast({ sev: "warning", msg: "No beds in this ward" }); return; }
    if (!pinFrom || !pinTo || pinTo < pinFrom) { setToast({ sev: "warning", msg: "Set a valid assignment window (To cannot be before From)" }); return; }
    const assignments = [];
    wardNurseIds.forEach((nid) => {
      beds.forEach((b) => {
        // NOTE: deliberately NOT sending department_id — the NurseBedAssignment
        // DB column is integer while the value here is the string department_id
        // ("general_practice_IN..."), which errors on insert. It isn't needed:
        // the (nurse, bed, shift) row is what drives coverage / My Patients, and
        // the single-bed assign path never sent it either.
        assignments.push({
          nurse_id: nid, bed_code: bedCodeOf(b),
          valid_from: pinFrom, valid_to: pinTo, shift: pinShift,
        });
      });
    });
    setWardBusy(true);
    try {
      const res = await bulkAssignBeds(assignments);
      if (res?.status !== "Success") { setToast({ sev: "error", msg: res?.message || "Ward assign failed" }); return; }
      // A "Success" envelope still carries per-row skipped (already active) and
      // errors — report the real counts, not just "done".
      const created = res?.data?.length ?? 0;
      const skipped = res?.skipped?.length ?? 0;
      const errors = res?.errors?.length ?? 0;
      await reloadAssignments();
      if (errors) {
        // Surface the actual reason, not just a count — the first error's
        // message (trimmed to one line) is what makes the toast actionable.
        const firstErr = String(res?.errors?.[0]?.error || "").split("\n")[0];
        setToast({ sev: "warning", msg: `${created} of ${created + errors} beds assigned — ${errors} failed${firstErr ? `: ${firstErr}` : ""}` });
      } else {
        const nurseLabel = wardNurseIds.length === 1
          ? (nurses.find((n) => n.nurse_id === wardNurseIds[0])?.name || "Nurse")
          : `${wardNurseIds.length} nurses`;
        setToast({ sev: "success", msg: `${nurseLabel} assigned to ${wardName} — ${created} bed${created === 1 ? "" : "s"}${skipped ? ` (${skipped} already assigned)` : ""}` });
      }
    } catch (err) {
      setToast({ sev: "error", msg: err?.response?.data?.message || "Ward assign failed" });
    } finally { setWardBusy(false); }
  };

  const doUnassignWard = async () => {
    if (!filters.room_category) { setToast({ sev: "warning", msg: "Select a Ward first" }); return; }
    if (!wardNurseIds.length) { setToast({ sev: "warning", msg: "Select at least one nurse" }); return; }
    const codeSet = new Set(wardBedCodes);
    const rows = assignments.filter((a) => wardNurseIds.includes(a.nurse_id) && codeSet.has(a.bed_code));
    if (!rows.length) { setToast({ sev: "warning", msg: "No matching assignments to remove" }); return; }
    setWardBusy(true);
    try {
      // Each row carries an id, so delete precisely (one shift-row each) rather
      // than the compound form, which would drop all shift-rows for the pair.
      const results = await Promise.allSettled(
        rows.map((a) => (a.id != null
          ? unassignBed({ id: a.id })
          : unassignBed({ nurse_id: a.nurse_id, bed_code: a.bed_code, shift: a.shift })))
      );
      const removed = results.filter((r) => r.status === "fulfilled" && r.value?.status === "Success").length;
      const failed = rows.length - removed;
      await reloadAssignments();
      setToast({ sev: failed ? "warning" : "success", msg: `${removed} removed${failed ? `, ${failed} failed` : ""}` });
    } catch (err) {
      setToast({ sev: "error", msg: "Ward unassign failed" });
    } finally { setWardBusy(false); }
  };

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2, justifyContent: "space-between", alignItems: { sm: "center" } }}>
        <Box>
          <Typography variant="h6">Nursing Manager · Bed Assignments</Typography>
          <Typography variant="body2" color="text.secondary">
            {mode === "bed"
              ? "Assign nurses to beds and transfer patients across wards."
              : "Assign nurse(s) to an entire ward in one step."}
          </Typography>
        </Box>
        {can(BED_DASHBOARD_ACTIONS.ASSIGN_NURSE) && (
          <ToggleButtonGroup
            size="small" exclusive value={mode}
            onChange={(_, next) => { if (next) setMode(next); }}
          >
            <ToggleButton value="bed">Bed</ToggleButton>
            <ToggleButton value="ward">Ward</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: "Total Beds", value: stats.total },
          { label: "Occupied", value: stats.occupied },
          { label: "Beds with Nurse", value: stats.withNurse },
          { label: "Active Nurses", value: stats.nurses },
        ].map((s) => (
          <Grid item xs={6} md={3} key={s.label}>
            <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
              <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              <Typography variant="h6">{s.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <WardRatioStrip beds={beds} />

      <Paper sx={{ p: 1.5, borderRadius: 2, mb: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <BedCensusFilters
          filters={filters} setFilters={setFilters}
          wards={wards} rooms={rooms}
          search={searchInput} onSearchChange={setSearchInput}
        />
      </Paper>

      {(loading || bedsLoading) && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Nurses</Typography>
            <Stack spacing={1} sx={{ maxHeight: 520, overflowY: "auto" }}>
              {nurses.map((n) => {
                const selected = selectedNurseId === n.nurse_id;
                return (
                  <Paper key={n.nurse_id} onClick={() => setSelectedNurseId(n.nurse_id)}
                    sx={{ p: 1.5, cursor: "pointer", borderRadius: 2, border: `2px solid ${selected ? NURSE_COLORS.accent : NURSE_COLORS.border}`, bgcolor: selected ? NURSE_COLORS.accentSoft : "#fff" }}>
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                      <Avatar sx={{ bgcolor: nurseAvatarColor(n.nurse_id) }}>{initials(n.name)}</Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap>{n.name}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>{[n.nurse_id, n.department].filter(Boolean).join(" · ")}</Typography>
                      </Box>
                      <Chip size="small" color="primary" label={`${nurseLoad(n.nurse_id)} beds`} />
                    </Stack>
                  </Paper>
                );
              })}
              {!loading && nurses.length === 0 && (<Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>No nurses found.</Typography>)}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1">Beds</Typography>
              {/* The assignment window applies to both modes, so it lives above
                  the mode-specific controls rather than being duplicated. */}
              {can(BED_DASHBOARD_ACTIONS.ASSIGN_NURSE) && (
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mt: 1, flexWrap: "wrap" }}>
                  <TextField
                    type="date" size="small" label="From" value={pinFrom}
                    onChange={(e) => setPinFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
                  />
                  <TextField
                    type="date" size="small" label="To" value={pinTo}
                    onChange={(e) => setPinTo(e.target.value)}
                    InputLabelProps={{ shrink: true }} inputProps={{ min: pinFrom || undefined }}
                    sx={{ minWidth: 150 }}
                  />
                  <TextField
                    select size="small" label="Shift" value={pinShift}
                    onChange={(e) => setPinShift(e.target.value)} sx={{ minWidth: 170 }}
                  >
                    {schedule.map((s) => (
                      <MenuItem key={s.code} value={s.code}>
                        {`${s.label} (${s.start_time}-${s.end_time})`}
                      </MenuItem>
                    ))}
                  </TextField>
                  {/* The dates are ROSTER dates, not calendar dates: a shift
                      running past midnight keeps the date it started on. Saying
                      so avoids the "why does it show yesterday?" reading at 4am. */}
                  <Typography variant="caption" color="text.secondary">
                    Applies only to this shift on these dates. In progress now:{" "}
                    <b>{nowShift.shift}</b> of <b>{nowShift.rosterDate}</b> — a shift
                    running past midnight keeps the date it began.
                  </Typography>
                </Stack>
              )}
              {mode === "bed" ? (
                <Stack direction="row" spacing={2} sx={{ alignItems: "center", mt: 0.5, flexWrap: "wrap" }}>
                  <Typography variant="caption" color="text.secondary">Click + / – to assign/remove <b>{selectedNurse?.name || "—"}</b></Typography>
                  <Typography variant="caption" color="text.secondary">
                    Optional — ward coverage comes from the roster; assign a bed only to pin a specific nurse.
                  </Typography>
                </Stack>
              ) : (
                <>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mt: 1, flexWrap: "wrap" }}>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel>Nurses</InputLabel>
                      <Select
                        multiple value={wardNurseIds}
                        onChange={(e) => setWardNurseIds(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
                        input={<OutlinedInput label="Nurses" />}
                        renderValue={(sel) => sel.map((id) => nurses.find((n) => n.nurse_id === id)?.name || id).join(", ")}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                      >
                        {nurses.map((n) => (
                          <MenuItem key={n.nurse_id} value={n.nurse_id}>
                            <Checkbox checked={wardNurseIds.indexOf(n.nurse_id) > -1} />
                            <ListItemText primary={n.name} secondary={[n.nurse_id, n.department].filter(Boolean).join(" · ")} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      disabled={wardBusy || !filters.room_category || !wardNurseIds.length || !wardBedCodes.length}
                      onClick={doAssignWard}
                    >
                      Assign ward{filters.room_category ? ` (${wardBedCodes.length} beds)` : ""}
                    </Button>
                    <Button
                      variant="outlined" color="error"
                      disabled={wardBusy || !filters.room_category || !wardNurseIds.length}
                      onClick={doUnassignWard}
                    >
                      Unassign ward
                    </Button>
                  </Stack>
                  <Typography variant="caption" color={filters.room_category ? "text.secondary" : "warning.main"} sx={{ display: "block", mt: 1 }}>
                    {filters.room_category
                      ? `${wardBedCodes.length} bed(s) in ${wardName} — each selected nurse is assigned to all of them.`
                      : "Select a Ward in the filters above to choose which beds to assign."}
                  </Typography>
                </>
              )}
            </Box>
            <BedCensusTable
              beds={beds}
              admissionByBed={admissionByBed}
              loading={bedsLoading}
              emptyMessage="No beds found."
              paginate
              rowsPerPageOptions={[25, 50, 100]}
              extraColumns={[{
                header: "Assigned Nurses",
                render: (bed) => {
                  const assignedNurses = assignmentsByBed[bedCodeOf(bed)] || [];
                  return (
                    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
                      {/* Each chip is individually removable — the delete acts on
                          the nurse shown, not on whoever happens to be selected. */}
                      {assignedNurses.map((a) => {
                        // The list keeps expired and future pins so a manager
                        // can plan, but they must not read as live coverage —
                        // dim them and say so.
                        const live = isPinInForce(a, nowShift.rosterDate, nowShift.shift);
                        return (
                          <Chip
                            key={a.id} size="small" variant="outlined"
                            sx={{ opacity: live ? 1 : 0.45 }}
                            title={live ? "In force this shift" : "Not in force this shift"}
                            label={`${a.nurse_name || nurseNameById[a.nurse_id] || a.nurse_id}${a.shift ? ` · ${a.shift}` : ""}${a.valid_to ? ` · to ${a.valid_to}` : ""}`}
                            onDelete={can(BED_DASHBOARD_ACTIONS.ASSIGN_NURSE) ? () => doUnassign(a) : undefined}
                          />
                        );
                      })}
                      {assignedNurses.length === 0 && (<Typography variant="caption" color="text.secondary">Unassigned</Typography>)}
                    </Stack>
                  );
                },
              }]}
              renderActions={mode !== "bed" ? undefined : (bed) => {
                const code = bedCodeOf(bed);
                // Occupancy check uses patientByBed (not the census hook's
                // admissionByBed) because that's what openTransfer() actually
                // populates the dialog from — using a different source here
                // could show the Transfer icon on a bed the dialog then
                // opens with no patient.
                const occupied = Boolean(patientByBed[code]);
                // Retention is backend-restricted to "available" beds
                // (manage_bed_retention.py), so gate the action on the bed's
                // own status rather than merely "not occupied" — a cleaning or
                // already-reserved bed would be rejected.
                const isAvailable = String(bed.status || "").toLowerCase() === "available";
                // Only need whether the SELECTED nurse is already here, to hide
                // the redundant "+". Removal is per-chip in the column above.
                const selectedAlreadyHere = Boolean(findAssignment(selectedNurseId, code));
                return (
                  <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
                    {occupied && can(BED_DASHBOARD_ACTIONS.TRANSFER_PATIENT) && (
                      <Tooltip title="Transfer patient"><span><IconButton color="secondary" onClick={() => openTransfer(bed)}><SwapHorizIcon /></IconButton></span></Tooltip>
                    )}
                    {isAvailable && can(BED_DASHBOARD_ACTIONS.RETAIN_BED) && (
                      <Tooltip title="Retain (reserve) this bed"><span><IconButton color="warning" onClick={() => setRetainCode(code)}><EventBusyIcon /></IconButton></span></Tooltip>
                    )}
                    {can(BED_DASHBOARD_ACTIONS.ASSIGN_NURSE) && !selectedAlreadyHere && (
                      <Tooltip title={selectedNurseId ? `Assign ${selectedNurse?.name || "selected nurse"}` : "Select a nurse first"}>
                        <span>
                          <IconButton color="primary" disabled={!selectedNurseId} onClick={() => doAssign(code)}><AddCircleIcon /></IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                );
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      <RaiseTransferRequestModal
        open={Boolean(xfer)}
        onClose={() => setXfer(null)}
        admissionId={xfer?.admissionId}
        fromBedCode={xfer?.fromBedCode}
        fromBed={xfer?.fromBed}
        onRaised={handleRaised}
      />

      <RetainBedModal
        open={Boolean(retainCode)}
        bedCode={retainCode}
        onClose={() => setRetainCode(null)}
        onRetained={() => { setRetainCode(null); reloadBeds(); }}
      />

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)} variant="filled">{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
